import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { Close, DeleteOutline, Send, WarningAmberOutlined } from "@mui/icons-material";
import { useState, useContext } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { ManagedAccountContext } from "../../context/ManagedAccountContext";

const WSU_RED = "#A60F2D";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const DeleteManagedAccount = () => {
  const [state, setState] = useState({ waiting: false, error: null, comment: "", message: null });

  const { setModule } = useContext(ModuleContext);
  const { managedAccountModule, setManagedAccountModule } = useContext(ManagedAccountContext);
  const { authState } = useOktaAuth();

  const deleteManagedAccount = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/managed-accounts/delete`,
        {
          method: "POST",
          headers: {
            Authorization: authState.accessToken.accessToken,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            callerId: authState.accessToken.claims.sub,
            comment: state.comment,
            dn: managedAccountModule.dn,
            userPrincipalName: managedAccountModule.upn,
          }),
        }
      );

      if (!response.ok) {
        const errorObj = await response.json();
        throw new Error(errorObj?.error.message || "Delete request failed");
      }

      setState((prev) => ({
        ...prev,
        waiting: false,
        error: null,
        message: `Managed Account ${managedAccountModule.upn} deleted`,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error: { message: error.message } }));
    }
  };

  const closeContainer = () => {
    setModule((prev) => ({ ...prev, isVisible: false }));
    const updatedResults =
      managedAccountModule.searchResults?.filter(
        (r) => r.userPrincipalName !== managedAccountModule.upn
      ) || [];
    setManagedAccountModule((prev) => ({ ...prev, isOpen: false, searchResults: updatedResults }));
  };

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.message) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <DeleteOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Account Deleted</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Confirmation
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">{state.message}</Typography>
        </Box>
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            endIcon={<Close />}
            onClick={closeContainer}
            sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
          >
            Close
          </Button>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* Error Banner */}
      {state.error && (
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Delete failed</Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      )}

      {/* Warning Banner */}
      <Box sx={{ backgroundColor: "#fff3e0", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <WarningAmberOutlined sx={{ color: "#e65100" }} fontSize="small" />
        <Typography variant="body2" sx={{ color: "#e65100", fontWeight: 600 }}>
          This action is permanent and cannot be undone.
        </Typography>
      </Box>

      {/* ── Section: Confirm Deletion ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Confirm Deletion
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
          {managedAccountModule.upn}
        </Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      {/* ── Section: Justification ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Justification
        </Typography>
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>
        <TextField
          required
          fullWidth
          size="small"
          multiline
          rows={3}
          label="Comment"
          value={state.comment}
          onChange={(e) => setState((prev) => ({ ...prev, comment: e.target.value }))}
          placeholder="Provide a reason for deleting this account"
          sx={fieldSx}
        />
      </Box>

      {/* ── Actions ── */}
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          endIcon={<Close />}
          onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
          sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={deleteManagedAccount}
          disabled={!state.comment.trim()}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
            "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
          }}
        >
          Delete Account
        </Button>
      </Box>

    </TableContainer>
  );
};
